import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { TrendingUp, Users, Star, Heart, ArrowUpRight, Sparkles, Instagram, QrCode } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";

const AIMetrics = () => {
  const { language } = useLanguage();

  const engagementData = [
    { week: "Sem 1", reviews: 12, followers: 45, scans: 89 },
    { week: "Sem 2", reviews: 18, followers: 67, scans: 124 },
    { week: "Sem 3", reviews: 25, followers: 98, scans: 167 },
    { week: "Sem 4", reviews: 31, followers: 142, scans: 203 },
    { week: "Sem 5", reviews: 42, followers: 189, scans: 256 },
    { week: "Sem 6", reviews: 58, followers: 234, scans: 312 },
  ];

  const topBusinesses = [
    { rank: 1, name: language === "fr" ? "Café de Paris" : "Café de Paris", reviews: 127, change: "+23%" },
    { rank: 2, name: "Le Petit Bistro", reviews: 98, change: "+18%" },
    { rank: 3, name: "Boulangerie Martin", reviews: 84, change: "+12%" },
    { rank: 4, name: "Salon Élégance", reviews: 67, change: "+9%" },
  ];

  const recentActivity = [
    { action: language === "fr" ? "Nouvel avis 5★ sur Google" : "New 5★ Google review", time: "2 min", icon: Star },
    { action: language === "fr" ? "+3 abonnés Instagram" : "+3 Instagram followers", time: "8 min", icon: Instagram },
    { action: language === "fr" ? "15 scans QR code" : "15 QR code scans", time: "1h", icon: QrCode },
  ];

  return (
    <section className="py-12 lg:py-16 bg-slate-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose-100/70 to-violet-100/70 border border-rose-200/50 backdrop-blur-sm mb-4">
            <Sparkles className="w-4 h-4 text-rose-500" />
            <span className="text-sm font-medium bg-gradient-to-r from-rose-500 to-violet-500 bg-clip-text text-transparent">
              {language === "fr" ? "Dashboard Analytics" : "Analytics Dashboard"}
            </span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mb-4 text-slate-800">
            {language === "en" ? (
              <>
                <span className="bg-gradient-to-r from-rose-400 via-pink-500 to-violet-500 bg-clip-text text-transparent animate-gradient-shine">Customer Engagement</span> Metrics
              </>
            ) : (
              <>
                Métriques d'<span className="bg-gradient-to-r from-rose-400 via-pink-500 to-violet-500 bg-clip-text text-transparent animate-gradient-shine">engagement client</span>
              </>
            )}
          </h2>
          
          <p className="text-base text-slate-500 max-w-xl mx-auto mb-6">
            {language === "fr" 
              ? "Suivez vos avis, abonnés et interactions en temps réel."
              : "Track your reviews, followers and interactions in real-time."}
          </p>
          
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-rose-500 to-violet-500 hover:from-rose-400 hover:to-violet-400 text-white font-semibold px-6 py-5 rounded-full shadow-lg shadow-rose-500/25 transition-all duration-300 hover:scale-105"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {language === "fr" ? "Démarrer maintenant" : "Get started now"}
            <ArrowUpRight className="ml-2 h-5 w-5" />
          </Button>
        </AnimatedSection>

        {/* Top Row - Engagement Chart & Business Ranking */}
        <div className="grid lg:grid-cols-2 gap-5 mb-5">
          {/* Engagement Growth Chart */}
          <AnimatedSection animation="fade-up" delay={0.1}>
            <div className="group relative p-5 rounded-2xl card-highlight transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-rose-100 to-violet-100 border border-rose-200/50">
                      <TrendingUp className="h-5 w-5 text-rose-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {language === "fr" ? "Croissance engagement" : "Engagement Growth"}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === "fr" ? "Évolution de vos indicateurs clés" : "Evolution of your key indicators"}
                  </p>
                </div>

                {/* Line Chart */}
                <div className="relative h-48 lg:h-56">
                  <svg className="w-full h-full" viewBox="0 0 600 200">
                    {/* Gradient definitions */}
                    <defs>
                      <linearGradient id="reviewGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#f43f5e" />
                        <stop offset="50%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                      <linearGradient id="areaGradientRoseViolet" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
                      </linearGradient>
                      <filter id="glowFilterRoseViolet" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    
                    {/* Grid lines */}
                    {[0, 25, 50, 75, 100].map((y, i) => (
                      <line
                        key={i}
                        x1="40"
                        y1={180 - y * 1.6}
                        x2="580"
                        y2={180 - y * 1.6}
                        stroke="rgba(148,163,184,0.2)"
                        strokeDasharray="4 4"
                      />
                    ))}

                    {/* Area fill */}
                    <path
                      d={`M70,${180 - engagementData[0].reviews * 2} ${engagementData.map((d, i) => `L${70 + i * 100},${180 - d.reviews * 2}`).join(" ")} L570,180 L70,180 Z`}
                      fill="url(#areaGradientRoseViolet)"
                    />

                    {/* Reviews Line */}
                    <polyline
                      points={engagementData.map((d, i) => `${70 + i * 100},${180 - d.reviews * 2}`).join(" ")}
                      fill="none"
                      stroke="url(#reviewGradient)"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter="url(#glowFilterRoseViolet)"
                    />
                    
                    {/* Data points */}
                    {engagementData.map((d, i) => (
                      <g key={i}>
                        <circle
                          cx={70 + i * 100}
                          cy={180 - d.reviews * 2}
                          r="10"
                          fill="rgba(244, 63, 94, 0.15)"
                        />
                        <circle
                          cx={70 + i * 100}
                          cy={180 - d.reviews * 2}
                          r="5"
                          fill="white"
                          stroke="url(#reviewGradient)"
                          strokeWidth="2.5"
                        />
                        {i === engagementData.length - 1 && (
                          <g>
                            <rect
                              x={70 + i * 100 - 20}
                              y={180 - d.reviews * 2 - 32}
                              width="40"
                              height="20"
                              rx="6"
                              fill="url(#reviewGradient)"
                            />
                            <text
                              x={70 + i * 100}
                              y={180 - d.reviews * 2 - 18}
                              textAnchor="middle"
                              className="text-[11px] font-bold fill-white"
                            >
                              {d.reviews}
                            </text>
                          </g>
                        )}
                      </g>
                    ))}
                    
                    {/* Followers line (secondary) */}
                    <polyline
                      points={engagementData.map((d, i) => `${70 + i * 100},${180 - d.followers * 0.6}`).join(" ")}
                      fill="none"
                      stroke="rgba(236,72,153,0.5)"
                      strokeWidth="2"
                      strokeDasharray="6 4"
                    />
                    
                    {/* Scans line (tertiary) */}
                    <polyline
                      points={engagementData.map((d, i) => `${70 + i * 100},${180 - d.scans * 0.5}`).join(" ")}
                      fill="none"
                      stroke="rgba(16,185,129,0.4)"
                      strokeWidth="2"
                      strokeDasharray="6 4"
                    />

                    {/* Labels */}
                    {engagementData.map((d, i) => (
                      <text
                        key={i}
                        x={70 + i * 100}
                        y="198"
                        textAnchor="middle"
                        className="text-[10px] fill-slate-400"
                      >
                        {d.week}
                      </text>
                    ))}
                  </svg>
                </div>
                
                {/* Legend */}
                <div className="flex gap-4 justify-center mt-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-rose-400 to-violet-500" />
                    <span className="text-xs text-slate-700 font-medium">{language === "fr" ? "Avis" : "Reviews"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-pink-400/60" />
                    <span className="text-xs text-slate-500">{language === "fr" ? "Abonnés" : "Followers"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-400/50" />
                    <span className="text-xs text-slate-500">{language === "fr" ? "Scans" : "Scans"}</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Top Businesses */}
          <AnimatedSection animation="fade-up" delay={0.2}>
            <div className="group relative p-5 rounded-2xl card-highlight transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200/50">
                      <Star className="h-5 w-5 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {language === "fr" ? "Top commerces" : "Top Businesses"}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === "fr" ? "Classement par avis collectés" : "Ranked by collected reviews"}
                  </p>
                </div>

                <div className="space-y-2">
                  {topBusinesses.map((item) => (
                    <div
                      key={item.rank}
                      className={`flex items-center justify-between p-3 rounded-xl transition-all duration-300 ${
                        item.rank === 1
                          ? "bg-gradient-to-r from-rose-50 to-violet-50 border border-rose-200/60"
                          : "bg-slate-50/80 border border-slate-200/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                          item.rank === 1 
                            ? "bg-gradient-to-br from-rose-400 to-violet-500 text-white shadow-lg shadow-rose-500/30" 
                            : "bg-slate-100 text-slate-500"
                        }`}>
                          #{item.rank}
                        </div>
                        <div>
                          <div className={`font-medium text-sm ${item.rank === 1 ? "text-slate-800" : "text-slate-600"}`}>
                            {item.name}
                          </div>
                          <div className="text-xs text-emerald-500">
                            {item.change}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${item.rank === 1 ? "bg-gradient-to-r from-rose-500 to-violet-500 bg-clip-text text-transparent" : "text-slate-500"}`}>
                          {item.reviews}
                        </div>
                        <div className="text-xs text-slate-400">{language === "fr" ? "avis" : "reviews"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Bottom Row - Satisfaction, Rating, Activity */}
        <div className="grid md:grid-cols-3 gap-5">
          {/* Customer Satisfaction */}
          <AnimatedSection animation="fade-up" delay={0.3}>
            <div className="group relative p-5 rounded-2xl card-highlight transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-green-100 border border-emerald-200/50">
                      <Heart className="h-5 w-5 text-emerald-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {language === "fr" ? "Satisfaction" : "Satisfaction"}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === "fr" ? "Répartition des avis" : "Review distribution"}
                  </p>
                </div>

                <div className="flex items-center justify-center gap-4 mt-4">
                  {[
                    { value: 78, label: "5★", color: "bg-emerald-100 text-emerald-600 border-emerald-200" },
                    { value: 15, label: "4★", color: "bg-amber-100 text-amber-600 border-amber-200" },
                    { value: 7, label: "≤3★", color: "bg-slate-100 text-slate-500 border-slate-200" },
                  ].map((rating, i) => (
                    <div key={i} className="text-center">
                      <div className={`w-14 h-14 lg:w-16 lg:h-16 rounded-xl ${rating.color} border-2 flex items-center justify-center mb-2`}>
                        <span className="text-lg lg:text-xl font-bold">{rating.value}%</span>
                      </div>
                      <span className="text-xs text-slate-500">{rating.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Average Rating */}
          <AnimatedSection animation="fade-up" delay={0.4}>
            <div className="group relative p-5 rounded-2xl card-highlight transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200/50">
                      <Star className="h-5 w-5 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {language === "fr" ? "Note moyenne" : "Average Rating"}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === "fr" ? "Sur tous vos établissements" : "Across all locations"}
                  </p>
                </div>

                <div className="flex flex-col items-center justify-center mt-4">
                  <div className="text-5xl font-display font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent mb-2">
                    4.7
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-5 h-5 ${star <= 4 ? 'text-amber-400 fill-amber-400' : 'text-amber-400 fill-amber-200'}`} 
                      />
                    ))}
                  </div>
                  <span className="text-xs text-slate-500 mt-2">
                    {language === "fr" ? "Basé sur 847 avis" : "Based on 847 reviews"}
                  </span>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Recent Activity */}
          <AnimatedSection animation="fade-up" delay={0.5}>
            <div className="group relative p-5 rounded-2xl card-highlight transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 border border-violet-200/50">
                      <Users className="h-5 w-5 text-violet-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {language === "fr" ? "Activité récente" : "Recent Activity"}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === "fr" ? "Dernières interactions" : "Latest interactions"}
                  </p>
                </div>

                <div className="space-y-2">
                  {recentActivity.map((item, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/50 flex items-center gap-3"
                    >
                      <div className="p-2 rounded-lg bg-violet-100">
                        <item.icon className="w-4 h-4 text-violet-500" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-slate-700">{item.action}</span>
                      </div>
                      <span className="text-xs text-slate-400">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default AIMetrics;